import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ErrorOutlineRounded,
  KeyboardArrowDownRounded,
} from '@mui/icons-material';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import { financialSupportApplicationSchema } from './validationSchemas';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';
import { getFinancialSupportPlans } from './login.service';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const fieldGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const summaryRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr)' },
  gap: 2,
  alignItems: 'start',
};

const renderFieldLabel = (label, required = false) => (
  <Typography variant="subtitle2" sx={fieldLabelStyles}>
    {label}
    {required ? (
      <Box component="span" sx={{ color: 'error.main' }}>
        {' '}
        *
      </Box>
    ) : null}
  </Typography>
);

export default function SignupFinancialSupport({
  handleBack,
  handleChange,
  handleApply,
  formData,
  selectedPlanFinancialSupportDetails = [],
  selectedBillingCycle = 'monthly',
  subscriptionPlanTotal = 0,
}) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [supportTypes, setSupportTypes] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [prefilledType, setPrefilledType] = useState(null);
  const [prefilledPlanIds, setPrefilledPlanIds] = useState([]);

  const selectedType = formData.financialSupportType || '';

  // Extract financial support products from selected subscription plan
  const linkedProductIds = useMemo(() => {
    if (selectedPlanFinancialSupportDetails?.length > 0) {
      const selectedCycleDetails = selectedPlanFinancialSupportDetails.find(
        detail => detail.billingType?.toUpperCase() === selectedBillingCycle?.toUpperCase()
      );

      if (selectedCycleDetails?.financialSupportProductGroups?.length > 0) {
        const allProductIds = [];
        selectedCycleDetails.financialSupportProductGroups.forEach(group => {
          if (group.productIds && Array.isArray(group.productIds)) {
            allProductIds.push(...group.productIds);
          }
        });
        return allProductIds;
      }
    }
    return [];
  }, [selectedPlanFinancialSupportDetails, selectedBillingCycle]);

  // Set prefilled type and plan IDs for editing
  useEffect(() => {
    if (selectedPlanFinancialSupportDetails?.length > 0) {
      const selectedCycleDetails = selectedPlanFinancialSupportDetails.find(
        detail => detail.billingType?.toUpperCase() === selectedBillingCycle?.toUpperCase()
      );

      if (selectedCycleDetails?.financialSupportProductGroups?.length > 0) {
        const firstGroup = selectedCycleDetails.financialSupportProductGroups[0];
        setPrefilledType(firstGroup.supportType);
        setPrefilledPlanIds(firstGroup.productIds || []);
      } else {
        setPrefilledType(null);
        setPrefilledPlanIds([]);
      }
    } else {
      setPrefilledType(null);
      setPrefilledPlanIds([]);
    }
  }, [selectedPlanFinancialSupportDetails, selectedBillingCycle]);

  // Fetch financial support plans from database
  useEffect(() => {
    const loadFinancialSupportPlans = async () => {
      setLoading(true);
      try {
        const response = await getFinancialSupportPlans(true);

        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const plans = Array.isArray(response?.data) ? response.data : [];

          // Map API response to component format
          const mappedPlans = plans
            .filter(plan => plan.active !== false)
            .map(plan => ({
            id: plan.id,
            label: plan.name,
            productName: plan.name,
            productType: plan.supportType?.toLowerCase() || '',
            type: plan.supportType?.toLowerCase() || '',
            providerName: plan.organizationName || '-',
            amount: plan.amount,
            currency: plan.currency || '',
            interestRate: plan.interestRate ? `${plan.interestRate}%` : '0%',
            repaymentSchedule: plan.installments ? `${plan.installments} installments` : '-',
            coveragePercentage: plan.coveragePercentage,
            conditions: plan.conditions ? (typeof plan.conditions === 'string' ? JSON.parse(plan.conditions) : plan.conditions) : [],
            isActive: plan.active,
          }));

          setAllPlans(mappedPlans);

          // Filter plans to only include those linked to the selected subscription plan
          const linkedPlans = mappedPlans.filter(plan => linkedProductIds.includes(plan.id));

          // Extract unique support types from linked plans
          const uniqueTypes = [...new Set(linkedPlans.map(p => p.productType))];
          const types = uniqueTypes.map(type => ({
            id: type,
            label: type
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join('/'),
          }));
          setSupportTypes(types);
        } else {
          // No plans from API - show empty state
          setSupportTypes([]);
          setAllPlans([]);
        }
      } catch (error) {
        // API error - show empty state
        setSupportTypes([]);
        setAllPlans([]);
      } finally {
        setLoading(false);
      }
    };

    loadFinancialSupportPlans();
  }, [linkedProductIds]);


  const availablePlans = useMemo(
    () => {
      const plansForType = allPlans.filter(plan => plan.productType === selectedType);
      // Only include plans that are linked to the selected subscription plan
      return plansForType.filter(plan => linkedProductIds.includes(plan.id));
    },
    [selectedType, allPlans, linkedProductIds]
  );

  const selectedPlan = useMemo(
    () => {
      const plan = allPlans.find(p => String(p.id) === String(formData.financialSupportPlanId));
      return plan || availablePlans[0];
    },
    [availablePlans, formData.financialSupportPlanId, allPlans]
  );

  const eligibilityPercentages = useMemo(() => {
    if (!selectedPlan?.coveragePercentage) {
      return { customerContribution: 20, programCoverage: 80 };
    }
    const programCoverage = Math.round(Number(selectedPlan.coveragePercentage));
    const customerContribution = 100 - programCoverage;
    return { customerContribution, programCoverage };
  }, [selectedPlan]);

  const isApplyDisabled = !selectedType || !formData.financialSupportPlanId;

  const setFieldValue = (name, value, type = 'text') => {
    handleChange({
      target: {
        name,
        value,
        checked: type === 'checkbox' ? value : undefined,
        type,
      },
    });
  };

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const validateField = async (name, value, overrides = {}) => {
    try {
      await financialSupportApplicationSchema.validateAt(name, {
        ...formData,
        ...overrides,
        [name]: value,
      });
      clearFieldError(name);
    } catch (error) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [name]: error.message,
      }));
    }
  };

  const validateStep = async () => {
    try {
      await financialSupportApplicationSchema.validate(formData, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const handleTypeChange = async (event) => {
    const value = event.target.value;

    setTouched((currentTouched) => ({
      ...currentTouched,
      financialSupportType: true,
    }));

    setFieldValue('financialSupportType', value);
    // Clear plan selection when type changes to hide details/eligibility sections
    setFieldValue('financialSupportPlanId', '');
    setTouched((currentTouched) => ({
      ...currentTouched,
      financialSupportPlanId: false,
    }));
    await validateField('financialSupportType', value);
  };

  const handlePlanChange = async (event) => {
    const value = event.target.value;

    setTouched((currentTouched) => ({
      ...currentTouched,
      financialSupportPlanId: true,
    }));

    setFieldValue('financialSupportPlanId', value);
    await validateField('financialSupportPlanId', value);
  };

  const handleCancel = () => {
    // Only clear selections if financial support has NOT been applied for yet
    if (!formData.financialSupportRequested) {
      setFieldValue('financialSupportType', '');
      setFieldValue('financialSupportPlanId', '');
      setErrors({});
      setTouched({});
    }
    handleBack();
  };


  const handleApplySupport = async (event) => {
    event.preventDefault();

    setTouched({
      financialSupportType: true,
      financialSupportPlanId: true,
    });

    const isValid = await validateStep();
    if (!isValid) {
      return;
    }

    // Populate plan details when user applies
    const plan = allPlans.find(p => String(p.id) === String(formData.financialSupportPlanId));
    if (plan) {
      setFieldValue('financialSupportPlanName', plan.label || '');
      setFieldValue('financialSupportProviderName', plan.providerName || '');
      setFieldValue('financialSupportRepaymentSchedule', plan.repaymentSchedule || '');
      setFieldValue('financialSupportInterestRate', plan.interestRate || '');
      setFieldValue('financialSupportAmount', plan.amount || '');
      setFieldValue('financialSupportConditions', plan.conditions || []);

      // Store coverage percentage and calculate coverage amounts
      const coveragePercentage = plan.coveragePercentage ? Math.round(Number(plan.coveragePercentage)) : 0;
      const coverageAmount = (subscriptionPlanTotal * coveragePercentage) / 100;
      const userPaymentAmount = subscriptionPlanTotal - coverageAmount;

      setFieldValue('financialSupportCoveragePercentage', coveragePercentage);
      setFieldValue('financialSupportCoverageAmount', coverageAmount);
      setFieldValue('financialSupportUserPaymentAmount', userPaymentAmount);
    }

    setFieldValue('financialSupportRequested', true, 'checkbox');
    handleApply();
  };

  return (
    <Stack spacing={4} sx={{ maxWidth: 760, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={3} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
          <Stack spacing={3}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                Financial Support Application
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={authSubtextStyles}
              >
                Explore our financial assistance options and select the plan that best
                suits your needs.
              </Typography>
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : supportTypes.length === 0 || allPlans.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: '#E4E6F1',
                  px: 3,
                  py: 3,
                  backgroundColor: '#fafafa',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No financial support products are currently available. Please check back later or contact support.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Type', true)}
                  <TextField
                    select
                    value={selectedType}
                    onChange={handleTypeChange}
                    error={touched.financialSupportType && Boolean(errors.financialSupportType)}
                    helperText={
                      touched.financialSupportType && errors.financialSupportType
                        ? errors.financialSupportType
                        : undefined
                    }
                    SelectProps={{
                      displayEmpty: true,
                      IconComponent: KeyboardArrowDownRounded,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select type
                    </MenuItem>
                    {supportTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Select Financial Assistance Plan', true)}
                  <TextField
                    select
                    value={formData.financialSupportPlanId || ''}
                    onChange={handlePlanChange}
                    error={touched.financialSupportPlanId && Boolean(errors.financialSupportPlanId)}
                    helperText={
                    touched.financialSupportPlanId && errors.financialSupportPlanId
                      ? errors.financialSupportPlanId
                      : undefined
                  }
                  SelectProps={{
                    displayEmpty: true,
                    IconComponent: KeyboardArrowDownRounded,
                  }}
                >
                  <MenuItem value="" disabled>
                    Select financial assistance plan
                  </MenuItem>
                  {availablePlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              </Stack>
            )}

            {formData.financialSupportPlanId && (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'rgba(90, 143, 17, 0.30)',
                    background:
                      'linear-gradient(180deg, rgba(245, 252, 235, 1) 0%, rgba(252, 255, 248, 1) 100%)',
                    px: { xs: 2.5, sm: 3.5 },
                    py: { xs: 2.5, sm: 3.5 },
                  }}
                >
                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        backgroundColor: 'primary.main',
                        color: '#ffffff',
                      }}
                    >
                      <ErrorOutlineRounded sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Eligibility Requirement
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      To be eligible for financial support, you are required to pay:
                    </Typography>
                    <Stack spacing={0.25} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {eligibilityPercentages.customerContribution}% of Total Fee
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      The remaining {eligibilityPercentages.programCoverage}% will be covered by the support program.
                    </Typography>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: '#E4E6F1',
                    px: { xs: 2.5, sm: 3 },
                    py: { xs: 2.5, sm: 3 },
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Financial Assistance Plan Details
                    </Typography>

                    <Stack spacing={1.25}>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Type
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPlan
                            ? supportTypes.find(
                                (type) => type.id === selectedPlan.type
                              )?.label || supportTypes.find(
                                (type) => type.id === selectedPlan.productType
                              )?.label || selectedPlan.type || selectedPlan.productType
                            : '-'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Organization Name
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPlan?.providerName || '-'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Repayment Schedule
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPlan?.repaymentSchedule || '-'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Interest Rate
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPlan?.interestRate || '-'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Amount
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPlan?.amount && selectedPlan?.currency
                            ? `${selectedPlan.amount} ${selectedPlan.currency}`
                            : selectedPlan?.amount || '-'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Conditions
                      </Typography>
                      {selectedPlan?.conditions?.length ? (
                        <Stack spacing={1}>
                          {selectedPlan.conditions.map((condition, index) => (
                            <Box key={`${selectedPlan.id}-${index}`}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Condition {index + 1}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {condition}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Plan details will appear once you select an assistance option.
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                pt: 1,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={handleCancel}
                sx={{
                  order: { xs: 2, sm: 1 },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(90, 143, 17, 0.06)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleApplySupport}
                disabled={isApplyDisabled}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
}
